import { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react'
import { differenceInDays, format } from 'date-fns'

import { CenterModalCallbackContext } from 'context/popup/CenterModalProvider'
import { getDateByTimezone, redirectExternal } from 'utils/utils'

import useGetMotibellDetailQuery from 'hooks/queries/motibell/useGetMotibellDetailQuery'
import useJoinMotibellMutate from 'hooks/queries/motibell/useJoinMotibellMutate'
import useSubmitAnswerMutate from 'hooks/queries/motibell/useSubmitAnswerMutate'
import useUpdateUserPushConfigMutate from 'hooks/queries/user/useUpdateUserPushConfigMutate'
import useGetUserPushConfigQuery from 'hooks/queries/user/useGetUserPushConfigQuery'
import useUpdateMotibellViewCount from 'hooks/queries/motibell/useUpdateMotibellViewCount'
import useUpdateMotibellDailyStatisticMutate from 'hooks/queries/motibell/useUpdateMotibellDailyStatisticMutate'

import goldCoin from 'images/character/gold_coin-338×266.png'

import ConfirmCenterModal from 'components/presenters/popup/ConfirmCenterModal'

import BankAccountForm from 'components/modules/popup/CenterModal/BankAccountForm'

function useMotibellDetail({ motibellNo, userNo, showDailyQuiz, showDailyUserQuiz }) {
  const { openCenterModal, closeCenterModal, ref } = useContext(CenterModalCallbackContext)
  let isParticipatingRef = useRef(false)
  const answerRef = useRef(null)
  const displayPointRef = useRef(null)

  const [isQuizAlarm, setIsQuizAlarm] = useState(false)
  const [isSolved, setIsSolved] = useState(false)
  const [errorMsg, setErrorMsg] = useState(null)
  const [showFinishModal, setShowFinishModal] = useState(false)
  const { mutateAsync: joinMotibellMutateAsync } = useJoinMotibellMutate()
  const { mutateAsync: submitAnswer, isLoading: isSubmitLoading } = useSubmitAnswerMutate()
  const { mutate: updateUserPushConfig } = useUpdateUserPushConfigMutate(userNo)
  const { mutate: updateMotibellViewCountMutate } = useUpdateMotibellViewCount({ userNo })
  const { mutateAsync: updateMotibellDailyStatisticsMutateAsync } =
    useUpdateMotibellDailyStatisticMutate()
  const { data, refetch } = useGetMotibellDetailQuery(
    {
      motibellNo,
      showDailyQuiz,
      showDailyUserQuiz,
      showCapacity: true,
    },
    {
      cacheTime: 0,
      enable: !!motibellNo,
    },
  )
  const { data: userPushConfigData } = useGetUserPushConfigQuery(userNo, {
    enabled: !!userNo,
  })

  const openConfirmModal = useCallback(
    (dataList) => {
      openCenterModal({
        Component: (
          <ConfirmCenterModal
            whiteboxRef={ref}
            dataList={dataList}
            onComplete={(id) => closeCenterModal(id)}
          />
        ),
      })
    },
    [closeCenterModal, openCenterModal, ref],
  )
  useEffect(() => {
    if (data?.curr_participant) {
      isParticipatingRef.current = true
    }
    if (data?.daily_user_quiz) {
      setIsSolved(data.daily_user_quiz?.is_solved)
    }
  }, [data])

  const isFullCapacity = useMemo(() => {
    if (!data) return

    const maxCapacity = data.max_capacity
    const currCapacity = data.curr_capacity
    if (maxCapacity === null) {
      return false
    }
    return currCapacity === maxCapacity
  }, [data])

  const joinMotibell = useCallback(async () => {
    await joinMotibellMutateAsync(
      { motibellNo },
      {
        onSuccess: () => {
          refetch()
        },
        onError: (err) => {
          throw err
        },
      },
    )
  }, [joinMotibellMutateAsync, motibellNo, refetch])

  const handleSubmitAnswer = async () => {
    try {
      if (answerRef.current.value.length === 0) {
        setErrorMsg('정답을 기입해 주세요')
        return
      }
      const answer = [answerRef.current.value]
      const isParticipating = isParticipatingRef.current
      const targetQuiz = data.daily_quiz
      const motibellType = data.motibell_type

      if (isSubmitLoading) return

      if (isParticipating === false) {
        if (motibellType === 'shinhan' && !isFullCapacity) {
          openCenterModal({
            Component: (
              <BankAccountForm
                whiteBoxRef={ref}
                onClose={(id) => closeCenterModal(id)}
                onComplete={joinMotibell}
              />
            ),
          })
        } else {
          await joinMotibell()
        }
      } else {
        const submitRes = await submitAnswer({
          motibellNo,
          motibellQuizNo: targetQuiz.no,
          answer,
        }).catch((err) => {
          throw err
        })
        const insertedItem = submitRes?.data?.inserted_item
        if (insertedItem.is_solved === false) {
          answerRef.current.value = ''
          setErrorMsg('정답이 아니에요')
        } else {
          displayPointRef.current = insertedItem.point.point
          setErrorMsg(null)
        }
        refetch()
          .then((res) => {
            const targetMotibell = res?.data
            const targetDailyUserQuiz = targetMotibell?.daily_user_quiz
            if (targetDailyUserQuiz?.is_solved === true) {
              setShowFinishModal(true)
            }
          })
          .catch(() => {})
      }
    } catch (err) {
      const errorRes = err.response?.data
      const errorData = errorRes?.error?.res_data
      if (errorData?.detail_code === 'EXCEEDED_MAX_CAPACITY') {
        const dataList = {
          title: '선착순 마감',
          description: '해당 퀴즈는 인원이 마감되었어요',
        }
        openConfirmModal(dataList)
      } else if (errorData?.detail_code === 'DATA_ALREADY_EXISTS') {
        setErrorMsg('이미 참여한 퀴즈에요.')
      } else {
        setErrorMsg('참여에 실패했어요. 나중에 다시 시도해주세요.')
      }
    }
  }

  const onChangeAnswer = (data) => {
    setErrorMsg(null)
  }

  const onChangeAlarmConfig = (e) => {
    const { checked } = e.target
    updateUserPushConfig(
      { quiz_alarm: checked },
      {
        onSuccess: (result) => {
          const { quiz_alarm: quizAlarm } = result.data.updated_item[0]
          setIsQuizAlarm(quizAlarm)
        },
      },
    )
  }

  const onClickExternalLink = useCallback(
    async (e, hintLink) => {
      e.preventDefault()
      await updateMotibellDailyStatisticsMutateAsync({ motibellNo, cntNames: ['hint_click_cnt'] })

      redirectExternal(hintLink)
    },
    [motibellNo, updateMotibellDailyStatisticsMutateAsync],
  )

  useEffect(() => {
    if (motibellNo) updateMotibellViewCountMutate({ motibellNo })
  }, [updateMotibellViewCountMutate, motibellNo])

  useEffect(() => {
    if (userPushConfigData) {
      const { quiz_alarm: quizAlarm } = userPushConfigData.data.push_config
      setIsQuizAlarm(quizAlarm)
    }
  }, [userPushConfigData])

  const getBannerProps = ({ ...otherProps } = {}) => {
    if (!data) {
      return {
        ...otherProps,
      }
    }
    return {
      filename: data?.banner_img,
    }
  }

  const getQuestionFormProps = ({ ...otherProps } = {}) => {
    if (!data) {
      return {
        ...otherProps,
      }
    }
    const dailyQuiz = data?.daily_quiz
    const targetHint = dailyQuiz?.hint
    const question = dailyQuiz?.question
    const currDate = getDateByTimezone('Asia/Seoul', new Date())
    const dueDate = format(new Date(data.due_date), 'yyyy-MM-dd')
    const endDate = format(new Date(data.end_date), 'yyyy-MM-dd')
    const visible = currDate <= dueDate || (data?.curr_participant && currDate <= endDate)
    return {
      question,
      motibellNo,
      visible,
      isSolved,
      hintType: targetHint?.hint_type,
      hintLink: targetHint?.hint_link,
      answerInputRef: answerRef,
      onSubmitAnswer: handleSubmitAnswer,
      onChangeAnswer,
      onClickExternalLink,
      errorMsg: errorMsg,
    }
  }
  const getExpireFormProps = ({ ...otherProps } = {}) => {
    if (!data) {
      return {
        ...otherProps,
      }
    }

    const currDate = getDateByTimezone('Asia/Seoul', new Date())
    const dueDate = format(new Date(data.due_date), 'yyyy-MM-dd')
    const endDate = format(new Date(data.end_date), 'yyyy-MM-dd')
    const visible = !(currDate <= dueDate || (data?.curr_participant && currDate <= endDate))
    return {
      visible,
    }
  }
  const getRewardProps = ({ ...otherProps } = {}) => {
    if (!data) {
      return {
        ...otherProps,
      }
    }
    const rewardDescriptions = data?.reward_descriptions
    return {
      rewardDescriptions,
    }
  }

  const getSubmitFinishModalProps = ({ ...otherProps } = {}) => {
    if (!data) {
      return {
        ...otherProps,
      }
    }
    const targetParticipant = data?.curr_participant
    const currDate = getDateByTimezone('Asia/Seoul')
    const endDate = getDateByTimezone('Asia/Seoul', new Date(data.end_date))
    const startDate = getDateByTimezone('Asia/Seoul', new Date(data.start_date))
    const motibellMaxCnt =
      currDate > startDate
        ? differenceInDays(new Date(endDate), new Date(currDate)) + 1
        : data.max_cnt

    const maxCnt = targetParticipant ? targetParticipant.max_cnt : motibellMaxCnt
    const successCnt = targetParticipant ? targetParticipant.success_cnt : 0

    const dataList = {
      title: `총 ${successCnt}/${maxCnt}회 정답`,
      description: `축하해요 🎉\n정답을 맞혔어요!`,
      modalImg: goldCoin,
      point: displayPointRef.current,
      buttonText: '확인',
    }
    const onComplete = () => {
      setShowFinishModal(false)
    }
    return { visible: showFinishModal, dataList, onComplete }
  }

  const getProgressBarProps = ({ ...otherProps } = {}) => {
    if (!data) {
      return {
        ...otherProps,
      }
    }
    const targetParticipant = data?.curr_participant
    const currDate = getDateByTimezone('Asia/Seoul')
    const endDate = getDateByTimezone('Asia/Seoul', new Date(data.end_date))
    const startDate = getDateByTimezone('Asia/Seoul', new Date(data.start_date))
    const dueDate = getDateByTimezone('Asia/Seoul', new Date(data.due_date))
    const motibellMaxCnt =
      currDate > startDate
        ? differenceInDays(new Date(endDate), new Date(currDate)) + 1
        : data.max_cnt

    const reqCnt = targetParticipant ? targetParticipant.req_cnt : data.req_cnt
    const maxCnt = targetParticipant ? targetParticipant.max_cnt : motibellMaxCnt
    const successCnt = targetParticipant ? targetParticipant.success_cnt : 0
    const visible = !(!targetParticipant && currDate > dueDate)
    let message = ''
    if (
      (!targetParticipant && currDate > dueDate) ||
      (targetParticipant && reqCnt && motibellMaxCnt + successCnt < reqCnt) ||
      (targetParticipant &&
        reqCnt === null &&
        targetParticipant.success_cnt < targetParticipant.max_cnt &&
        currDate > endDate)
    ) {
      message = ''
    } else if (!targetParticipant) {
      if (data?.req_cnt) {
        message = `리워드까지 ${data.req_cnt}회 남았어요!`
      } else {
        message = `골든벨까지 ${motibellMaxCnt}회 남았어요!`
      }
    } else {
      if (targetParticipant.success_cnt === targetParticipant.max_cnt) {
        message =
          targetParticipant.max_cnt === targetParticipant.req_cnt
            ? `축하해요! 리워드를 받을 수 있어요!`
            : `대단해요! 골든벨을 울렸어요!`
      } else if (targetParticipant.req_cnt) {
        message =
          targetParticipant.success_cnt < targetParticipant.req_cnt
            ? `리워드까지 ${targetParticipant.req_cnt - targetParticipant.success_cnt}회 남았어요!`
            : '축하해요! 리워드를 받을 수 있어요!'
      } else {
        message = `골든벨까지 ${
          targetParticipant.max_cnt - targetParticipant.success_cnt
        }회 남았어요!`
      }
    }
    return { visible, reqCnt, maxCnt, successCnt, message }
  }
  const getNotificationBarProps = ({ ...otherProps } = {}) => {
    if (!data || !userPushConfigData) {
      return {
        ...otherProps,
      }
    }

    const currDate = getDateByTimezone('Asia/Seoul', new Date())
    const dueDate = format(new Date(data.due_date), 'yyyy-MM-dd')
    const endDate = format(new Date(data.end_date), 'yyyy-MM-dd')
    const visible = currDate <= dueDate || (data?.curr_participant && currDate <= endDate)

    return { visible, onChange: onChangeAlarmConfig, checked: isQuizAlarm }
  }
  return {
    getBannerProps,
    getQuestionFormProps,
    getExpireFormProps,
    getProgressBarProps,
    getRewardProps,
    getSubmitFinishModalProps,
    getNotificationBarProps,
  }
}
export default useMotibellDetail
